/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type ComponentPropsWithoutRef } from 'react';
import { EntryPointContainer, type PreloadedEntryPoint } from 'react-relay';
import {
	type JSErrorBoundaryProps,
	JSErrorBoundary,
} from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type {
	AnyEntryPoint,
	ComponentOfEntryPoint,
} from '@jira/platform__entry-point/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import OldPlaceholder, {
	PlaceholderWithNativeSuspense,
} from '@atlassian/jira-placeholder/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';

const Placeholder: typeof PlaceholderWithNativeSuspense = componentWithCondition(
	() => fg('jfp_magma_jira-entrypoint-placeholder'),
	PlaceholderWithNativeSuspense,
	OldPlaceholder,
);

type EntryPointContainerProps<TEntryPoint extends AnyEntryPoint> = ComponentPropsWithoutRef<
	typeof EntryPointContainer<PreloadedEntryPoint<ComponentOfEntryPoint<TEntryPoint>>>
>;
type PlaceholderProps = ComponentPropsWithoutRef<typeof Placeholder>;

export type InternalEntryPointContainerProps<TEntryPoint extends AnyEntryPoint> = {
	entryPointReference: EntryPointContainerProps<TEntryPoint>['entryPointReference'];
	errorAttributes?: JSErrorBoundaryProps['attributes'];
	errorFallback?: JSErrorBoundaryProps['fallback'];
	fallback?: PlaceholderProps['fallback'];
	id: JSErrorBoundaryProps['id'];
	packageName?: JSErrorBoundaryProps['packageName'];
	placeholderName?: PlaceholderProps['name'];
	runtimeProps: EntryPointContainerProps<TEntryPoint>['props'];
	teamName?: JSErrorBoundaryProps['teamName'];
};

export const InternalEntryPointContainer = <TEntryPoint extends AnyEntryPoint>({
	entryPointReference,
	errorAttributes,
	errorFallback,
	fallback,
	id,
	packageName,
	placeholderName,
	runtimeProps,
	teamName,
}: InternalEntryPointContainerProps<TEntryPoint>): React.JSX.Element => (
	/* eslint-disable-next-line jira/ufo/valid-labels */
	<UFOSegment name={id}>
		<JSErrorBoundary
			id={id}
			packageName={packageName}
			teamName={teamName}
			attributes={errorAttributes}
			fallback={errorFallback}
		>
			<Placeholder
				name={placeholderName ?? packageName ?? 'internal-entry-point-container'}
				fallback={fallback}
			>
				<EntryPointContainer entryPointReference={entryPointReference} props={runtimeProps} />
			</Placeholder>
		</JSErrorBoundary>
	</UFOSegment>
);
